import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import {
  Calendar,
  FileText,
  Building,
  User,
  UserCheck
} from "lucide-react";
import { parseCitationNames } from "../../utils/HelperFunction/textFormatting";

const CaseDetailCard = ({
  title,
  icon,
  iconBgColor = "#EFF6FF",
  iconColor = "#2563EB",
  borderColor = "#E2E8F0",
  hoverBorderColor = "#2563EB",
  caseItem = null,
  judges = null,
  children,
}) => {
  const formatCaseTitle = (title) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(title, "text/html");

    const formatTextContent = (text) => {
      return text
        .split("Honorable")[0]
        .toLowerCase()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    };

    const walkNodes = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.nodeValue = formatTextContent(child.nodeValue);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walkNodes(child);
        }
      });
    };

    walkNodes(doc.body);

    return doc.body.innerHTML;
  };

  const baseCaseInfoList = [
    {
      icon: User,
      iconColor: "#2563EB",
      primary: formatCaseTitle(
        (caseItem?.Title?.split(/versus|vs/i)[0] || "N/A").trim()
      ),
      secondary: "Appellant",
    },
    {
      icon: UserCheck,
      iconColor: "#DC2626",
      primary: formatCaseTitle(
        (caseItem?.Title?.split(/versus|vs/i)[1] || "N/A").trim()
      ),
      secondary: "Respondent",
    },
    {
      icon: Building,
      iconColor: "#0891B2",
      primary: caseItem?.Court,
      secondary: "Court",
    },
    {
      icon: Calendar,
      iconColor: "#059669",
      primary: caseItem?.Filing_Date || "N/A",
      secondary: "Filing Date",
    },
  ];

  const citationItems = parseCitationNames(caseItem?.Citation_Name).map(
    (citation) => ({
      icon: FileText,
      iconColor: "#D97706",
      primary: citation,
      secondary: "Citation",
    })
  );

  const caseInfoList = [...baseCaseInfoList, ...citationItems];

  const judgesList = judges?.map((judge, index) => ({
    avatar: judge
      .split(" ")
      .map((word) => word[0])
      .join(""),
    avatarBgColor: `hsl(${index * 45}, 70%, 45%)`,
    primary: judge,
    secondary: index === 0 ? "Chief Justice" : "Justice",
  }));

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#FFFFFF",
        border: `1px solid ${borderColor}`,
        borderRadius: "12px",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          transform: "translateY(-2px)",
          borderColor: hoverBorderColor,
        },
      }}
    >
      <Box
        sx={{
          p: 1.25,
          boxSizing: "border-box",
          borderBottom: "1px solid #E2E8F0",
          bgcolor: "#F8FAFC",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: iconBgColor,
            borderRadius: "8px",
            color: iconColor,
          }}
        >
          {React.createElement(icon, { size: 14 })}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#1E293B",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent
        sx={{
          p: 1.25,
          "&:last-child": {
            paddingBottom: 0,
          },
          boxSizing: "border-box",
          flex: { xs: "none", md: 1 },
          overflowY: { xs: "visible", md: "auto" },
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "4px",
          },
        }}
      >
        {caseInfoList?.length > 0 || judgesList?.length > 0 ? (
          <List
            dense
            disablePadding
            sx={{
              "& .MuiListItem-root": {
                px: 1,
                py: 0.35,
                borderRadius: "8px",
                mb: 0.25,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#F8FAFC",
                  transform: "translateX(4px)",
                  "& .MuiListItemIcon-root": {
                    transform: "scale(1.1)",
                  },
                },
              },
              "& .MuiListItemIcon-root": {
                minWidth: 32,
                transition: "transform 0.2s ease",
              },
              "& .MuiListItemText-primary": {
                fontSize: "0.8125rem",
                lineHeight: 1.3,
                color: "#1E293B",
                fontWeight: 500,
              },
              "& .MuiListItemText-secondary": {
                fontSize: "0.6875rem",
                lineHeight: 1.3,
                color: "#64748B",
                mt: 0.25,
              },
            }}
          >
            {(caseItem ? caseInfoList : judgesList)?.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {item.avatar ? (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "0.75rem",
                        bgcolor: item.avatarBgColor || "#E2E8F0",
                        border: "2px solid #FFFFFF",
                      }}
                    >
                      {item.avatar}
                    </Avatar>
                  ) : (
                    React.createElement(item.icon, { 
                      size: 12, 
                      color: item.iconColor || "#2563EB" 
                    })
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        color: "#1E293B",
                        lineHeight: 1.2,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {item.primary}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748B",
                        fontSize: "0.65rem",
                        display: "block",
                        mt: 0.25,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {item.secondary}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};

export default CaseDetailCard;
